
import {map} from 'rxjs/operators';
import {Injectable} from "@angular/core";
import {Patient} from "../models/patient";
import {PatientProxy} from "./patient.proxy";
import {Observable, of, throwError} from "rxjs";
import {PatientResponseService} from "../../services/patient.response.service";
import {MetadataService} from "../../metadata/services/metadata.service";
import {ClinicalResponseService} from "../../services/clinical.response.service";
import {IClinicalResponse} from "../../interfaces/clinical.response";
import {IClinicalRequest} from "../../interfaces/clinical.request";
import {ClinicalRequestService} from "../../services/clinical.request.service";
import {PatientId, PatientIdValidation} from "../models/patient.id";
import {IPatientInformation} from "../../../patient.search/interfaces/patient.information";
import {KendoDatetimeLocaleService} from "../../../../common/datetime/services/kendo.datetime.service";
import {Visit} from "../../visit/models/visit";
import {TypeHelper} from "../../../../common/utils";
import {ModelBuilder} from "../../../../dev/components/elad.forms2/services/model.builder";
/**
 * Created by shahar on 11-Jul-16.
 */

@Injectable({ providedIn: 'root' })
  export class PatientService {
	constructor(private proxy:PatientProxy,
				private patientResponseService:PatientResponseService,
				private datetimeFormatService:KendoDatetimeLocaleService,
				private metadata:MetadataService,
				private modelBuilder:ModelBuilder,
				private clinicalRequestService:ClinicalRequestService,
				private clinicalResponseService:ClinicalResponseService){
	}

	public generatePatientId(): Observable<PatientId> {
		return this.proxy.generatePatientId();
	}

	public getPatientIdsByPatientPersonKey(patientPersonKey: number): Observable<PatientId[]> {
		return this.proxy.getPatientIdsByPatientPersonKey(patientPersonKey);
	}

	public getPatient(pKey:number): Observable<Patient> {
		return this.proxy.getPatient(pKey).pipe(map((x)=>{
			this.patientResponseService.patientResponseProcess(x.patient);
			return x.patient;
		}));
	}

	// public getPortalPatient(): Observable<Patient> {
	// 	return this.proxy.getPortalPatient().pipe(map((x)=>{
	// 		this.patientResponseService.patientResponseProcess(x.patient);
	// 		return x.patient;
	// 	}));
	// }

	// public getEmptyPatientDetails(): Observable<Patient> {
	// 	return this.proxy.getEmptyPatientDetails().pipe(map((x)=>{
	// 		this.patientResponseService.patientResponseProcess(x.patient);
	// 		return x.patient;
	// 	}));
	// }


	public getPatientById(pId:string): Observable<Patient> {
		return this.proxy.getPatientById(pId).pipe(map((x)=>{
			this.patientResponseService.patientResponseProcess(x.patient);
			return x.patient;
		}));
	}

	public getPatientByIdAndIssuer(pId:string, issuerOfPid: number): Observable<Patient> {
		return this.proxy.getPatientByIdAndIssuer(pId, issuerOfPid).pipe(map((x)=>{
			this.patientResponseService.patientResponseProcess(x.patient);
			return x.patient;
		}));
	}

	public createCleanPatient(includeAddress:boolean,createEmptyPID=false) : Patient{
		let patient:Patient = this.modelBuilder.build(this.metadata.patient);
		TypeHelper.changePrototype(patient, Patient);
		patient.patientIdList = createEmptyPID?[{patientId:null,primary:'Y'}]:[];
		patient.scanDocument = [];
		patient.visit = null;
		if (includeAddress){
			patient.address=[];
		}
		return patient;
	}

	public getPatientByInformation(info:IPatientInformation): Observable<Patient> {
		let obs = info.issuerOfPid ?
			this.proxy.getPatientByIdAndIssuer(info.patientId, info.issuerOfPid) :
			this.proxy.getPatientById(info.patientId);

		return obs.pipe(map((x)=>{
			this.patientResponseService.patientResponseProcess(x.patient);

			if (info.firstName && info.firstName !== x.patient.firstName) {
				throw {'name' : 'PatientNotFoundError', 'message' : 'PatientFirstNameIncorrect',
					'args': {'a0': info.firstName, 'a1': x.patient.firstName}};
			}

			if (info.lastName && info.lastName !== x.patient.lastName) {
				throw {'name' : 'PatientNotFoundError', 'message' : 'PatientLastNameIncorrect',
					'args': {'a0': info.lastName, 'a1': x.patient.lastName}};
			}

			if (info.birthDate && (!x.patient.birthDate || info.birthDate.getTime() !== x.patient.birthDate.getTime())) {
				throw {'name' : 'PatientNotFoundError', 'message' : 'PatientBirthDateIncorrect',
					'args': {'a0': this.datetimeFormatService.date(info.birthDate),
						'a1': this.datetimeFormatService.date(x.patient.birthDate)}};

			}

			return x.patient;
		}));
	}

	public getPatientPictureByPatientPersonKey(patientPersonKey: number): Observable<string> {
		return this.proxy.getPatientPictureByPatientPersonKey(patientPersonKey).pipe(map((x) => {
			return URL.createObjectURL(x);
		}));
	}

	public getPatientVisitList(patientPersonKey: number): Observable<Visit[]> {
		return this.proxy.getPatientVisitList(patientPersonKey);
	}

	public getAgeInYears(birthDate: any, deathDate: any, deceased: boolean): number {
		let startDate = birthDate instanceof Date ? birthDate : new Date(birthDate);
		let endDate = new Date();
		if (deceased && deathDate) {
			endDate = deathDate instanceof Date ? deathDate : new Date(deathDate);
		}

		let years = endDate.getFullYear() - startDate.getFullYear();
		let months = endDate.getMonth() - startDate.getMonth();
		if (months > 0) {
			years++;
		} else if (months === 0) {
			let days = endDate.getUTCDate() - startDate.getUTCDate();
			if (days > 1) {
				years++;
			}
		}

		return years;
	}

	public isPatientIdValidAndNotInUse(pId: string, pIdIssuer: number): Observable<PatientIdValidation> {
		if (!pId) {
			return of(PatientIdValidation.Unknown);
		} else {
			return this.proxy.isPatientIdValidAndNotInUse(pId, pIdIssuer);
		}
	}

	public updatePatient(req:IClinicalRequest) : Observable<IClinicalResponse> {
		let reqToSend = this.clinicalRequestService.clinicalRequestProcess(this.metadata.patient, req);

		return this.proxy.updatePatient(reqToSend).pipe(map((res:IClinicalResponse) => {
			this.clinicalResponseService.clinicalResponseProcess(res);
			return res;
		}));
	}

	public updatePatientId(currentPatientId: string, currentIssuer: number, newPatientId: string, newIssuer: number): Observable<number> {
		return this.proxy.updatePatientId(currentPatientId, currentIssuer, newPatientId, newIssuer);
	}

}
